import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { Unsubscribe } from '@angular/fire/firestore';
import {
  AccountAccessMode,
  AccountV3DecisionStatus,
  GovernanceRecordView,
  ProtectedCapability,
  SupportRequestSurface,
  UpcomingSessionBooking,
  isAccountV3DecisionStatus,
  isCapabilityAllowed,
  resolveAccountAccessMode,
} from 'src/app/domain/governance';
import { GovernanceService, SubmitDecisionResult } from 'src/app/domain/governance/services/governance.service';

/**
 * Signal-based state for the ACTIVE account's V3 governance record.
 *
 * Manages: the real-time governance record for the active partner account,
 * the derived access mode, and Lead Administrator visibility flags.
 * Initialization: `loadForAccount(partnerId, userEmail)` is called whenever
 * the active account changes (account init / team switch); calling it again
 * is safe — the prior listener is torn down first, and stale greater
 * capability is revoked immediately (state resets to indeterminate).
 * Reset: `clear()` on sign-out.
 *
 * Access-mode semantics (fail closed, but rollout-safe):
 *   - not loaded yet            → null (indeterminate — consumers wait)
 *   - listener error            → 'V2_COMPATIBILITY_MODE' (fail closed: we
 *                                 could not verify, so never grant more)
 *   - loaded, no record         → null (account not in the governance
 *                                 program yet — pre-backfill accounts keep
 *                                 current behavior; no enforcement)
 *   - loaded with record        → resolveAccountAccessMode(status): only an
 *                                 exact ACCEPTED yields FULL_V3
 */
@Injectable({ providedIn: 'root' })
export class GovernanceState implements OnDestroy {
  private readonly governanceService = inject(GovernanceService);
  private recordUnsubscribe?: Unsubscribe;
  private bookingsUnsubscribe?: Unsubscribe;

  // ── Internal writable signals ──────────────────────────────────────────

  private readonly _record = signal<GovernanceRecordView | null>(null);
  private readonly _loaded = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _accountId = signal<string | null>(null);
  private readonly _userEmail = signal<string | null>(null);
  private readonly _supportPrompt = signal<'ACCEPTED' | 'DECLINED' | null>(null);
  private readonly _bookedSessions = signal<UpcomingSessionBooking[]>([]);

  // ── Public readonly signals ────────────────────────────────────────────

  readonly record = this._record.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly error = this._error.asReadonly();
  readonly accountId = this._accountId.asReadonly();
  /**
   * Post-decision support prompt to show (server-confirmed status). Lives in
   * the STORE, not the decision component: an Accept immediately flips
   * showDecisionControls() false and unmounts the decision surface — the
   * prompt must survive that. Cleared by dismissSupportPrompt().
   */
  readonly supportPrompt = this._supportPrompt.asReadonly();

  /**
   * The soonest provider-confirmed upcoming session for the active account
   * (86e2e3ybv slice 2), or null when none. Purely informational — booking
   * surfaces swap their book button for the scheduled time + self-serve
   * cancel/reschedule links; booking state never gates access.
   */
  readonly upcomingBooking = computed<UpcomingSessionBooking | null>(() => {
    const now = Date.now();
    const upcoming = this._bookedSessions()
      .filter(booking => Date.parse(booking.eventStartTime) > now)
      .sort((a, b) => Date.parse(a.eventStartTime) - Date.parse(b.eventStartTime));
    return upcoming[0] ?? null;
  });

  // ── Computed ───────────────────────────────────────────────────────────

  /** True when the active account has a governance record (is in the V3 program). */
  readonly inProgram = computed(() => this._loaded() && this._record() !== null);

  /** The account's decision status, narrowed; null when absent or unrecognized. */
  readonly decisionStatus = computed<AccountV3DecisionStatus | null>(() => {
    const status = this._record()?.status;
    return isAccountV3DecisionStatus(status) ? status : null;
  });

  /** Access mode per the semantics documented on the class. */
  readonly accessMode = computed<AccountAccessMode | null>(() => {
    if (this._error()) {
      return 'V2_COMPATIBILITY_MODE';
    }
    const record = this._loaded() ? this._record() : null;
    return record ? resolveAccountAccessMode(record.status) : null;
  });

  /** True when Compatibility Mode is actively enforced for this account. */
  readonly compatibilityModeActive = computed(() => this.accessMode() === 'V2_COMPATIBILITY_MODE');

  /** True when the signed-in user is the account's designated Lead Administrator. */
  readonly isLeadAdministrator = computed(() => {
    const lead = this._record()?.leadAdministratorEmail;
    const user = this._userEmail()?.toLowerCase();
    return !!lead && !!user && lead === user;
  });

  /**
   * True when the Accept/Decline controls may render: only the Lead
   * Administrator of an in-program account whose recognized status is
   * PENDING or DECLINED. Errors and unrecognized states show no controls.
   */
  readonly showDecisionControls = computed(() => {
    if (this._error() || !this.isLeadAdministrator()) {
      return false;
    }
    const status = this.decisionStatus();
    return status === 'PENDING' || status === 'DECLINED';
  });

  // ── Lifecycle ──────────────────────────────────────────────────────────

  /**
   * Start (or restart) the governance listener for the active account.
   * Tears down any prior listener first; state resets to indeterminate so
   * a previously-granted FULL_V3 can never linger across an account switch.
   */
  loadForAccount(partnerId: string, userEmail: string): void {
    this.teardown();
    this._record.set(null);
    this._loaded.set(false);
    this._error.set(null);
    this._accountId.set(partnerId);
    this._userEmail.set(userEmail);

    this.recordUnsubscribe = this.governanceService.subscribeToGovernanceRecord(
      partnerId,
      record => {
        this._record.set(record);
        this._loaded.set(true);
        this._error.set(null);
      },
      error => {
        console.error('[GovernanceState] governance listener failed', error);
        this._error.set(error.message || 'Failed to load account governance state.');
        this._loaded.set(true);
      },
    );

    this.bookingsUnsubscribe = this.governanceService.subscribeToBookedSessions(
      partnerId,
      bookings => this._bookedSessions.set(bookings),
      error => {
        // Informational-only data: log and degrade to the book-button flow.
        console.error('[GovernanceState] booked-sessions listener failed', error);
        this._bookedSessions.set([]);
      },
    );
  }

  /** Reset everything (sign-out). */
  clear(): void {
    this.teardown();
    this._record.set(null);
    this._loaded.set(false);
    this._error.set(null);
    this._accountId.set(null);
    this._userEmail.set(null);
    this._supportPrompt.set(null);
    this._bookedSessions.set([]);
  }

  /**
   * Submit Accept/Decline for the active account as its Lead Administrator.
   * Passes the currently-known status as the stale-client guard; the snapshot
   * listener applies the authoritative result.
   */
  async submitDecision(decision: 'ACCEPTED' | 'DECLINED'): Promise<SubmitDecisionResult> {
    const accountId = this._accountId();
    if (!accountId) {
      throw new Error('No active account — cannot submit a V3 decision.');
    }
    const expectedPriorStatus = this.decisionStatus() ?? undefined;
    // Set the prompt BEFORE awaiting: on fast backends the record listener can
    // deliver the new status before the command response returns, and the
    // decision surface must stay mounted (home renders it while a prompt is
    // pending) or the in-flight component is destroyed mid-await and opens a
    // zombie modal. Corrected to the server-confirmed status on success,
    // cleared on failure (no prompt for a decision that was not recorded).
    this._supportPrompt.set(decision);
    try {
      const result = await this.governanceService.submitDecision(accountId, decision, expectedPriorStatus);
      this._supportPrompt.set(result.status);
      return result;
    } catch (error) {
      this._supportPrompt.set(null);
      throw error;
    }
  }

  /** Close the support prompt. Never affects the decision or access state. */
  dismissSupportPrompt(): void {
    this._supportPrompt.set(null);
  }

  /**
   * Persist a support/info-session request for the ACTIVE account (86e2dwhcu).
   * Access-neutral by construction: it delegates to the request callable and
   * touches no governance state here — record, access mode, and prompt are
   * all left exactly as they were. Rejects when the server did not record
   * the request, so UI acknowledgements stay truthful. Resolves the request
   * id so callers can tag the scheduling link (utm tracking) for the
   * webhook's deterministic booking match (86e2e3ybv).
   */
  async requestInfoSession(surface: SupportRequestSurface): Promise<string> {
    const accountId = this._accountId();
    if (!accountId) {
      throw new Error('No active account — cannot request an information session.');
    }
    return this.governanceService.requestInfoSession(accountId, surface);
  }

  /**
   * Consults the centralized capability policy (86e2dbnbj) for the ACTIVE
   * account's access mode. The Firestore rules enforce the same matrix at
   * the authoritative boundary — this gate is the client-side seam so UI and
   * services never hand-roll mode checks.
   */
  can(capability: ProtectedCapability): boolean {
    return isCapabilityAllowed(this.accessMode(), capability);
  }

  ngOnDestroy(): void {
    this.teardown();
  }

  private teardown(): void {
    if (this.recordUnsubscribe) {
      this.recordUnsubscribe();
      this.recordUnsubscribe = undefined;
    }
    if (this.bookingsUnsubscribe) {
      this.bookingsUnsubscribe();
      this.bookingsUnsubscribe = undefined;
    }
  }
}
